/**
 * Creature upgrades: what a stack can become, and what that costs.
 *
 * Two places in the game upgrade a standing stack - a Warcraft Keep on the map and
 * an upgraded dwelling in a town - and they differ only in *which* upgrades
 * they permit and *what fraction* of the price they charge. Everything else is
 * the same rule, so it lives here rather than twice.
 *
 * This module is a leaf on purpose. `services.ts` (Warcraft Keep) and `town.ts`
 * (the town screen) both need it, and the previous home for the logic was
 * `services.ts` itself; importing it from `town.ts` would have drawn
 * `town.ts -> services.ts`, and `services.ts` already reaches the town through
 * `objects.ts`. Nothing here imports anything from `adventure/` beyond ids and
 * state, which is what keeps that from becoming a cycle.
 *
 * ## The price
 *
 * The classic charges the *difference* between the two creatures' costs, per
 * creature, in every resource - upgrading a Spearwarden (60 gold) to a Glaivewarden
 * (75 gold) costs 15 gold each, not 75. A resource the upgrade needs *less* of
 * is not refunded, which is why the per-resource difference is floored at zero
 * rather than summed as a signed total.
 *
 * A town charges that difference in full. A Warcraft Keep discounts the lower tiers
 * (see `HILL_FORT_TIER_BP`), which is the whole reason to walk to one.
 */

import type { CreatureDefId } from "../ids.js";
import type { Resources } from "../state.js";
import { RESOURCES } from "../state.js";
import type { ContentRegistry, CreatureDef } from "../content/registry.js";

/** Full price, in basis points - what a town charges. */
export const FULL_PRICE_BP = 10000;

/**
 * What a Warcraft Keep charges, by the *base* creature's tier, in basis points.
 *
 * The classic: level 1 free, then 25% / 50% / 75%, and full price from level 5 up.
 * Indexed by tier, so slot 0 is unused and exists only to keep `tier` a direct
 * index rather than a `tier - 1` that has to be remembered at every call.
 */
export const HILL_FORT_TIER_BP: readonly number[] = Object.freeze([
  0, 0, 2500, 5000, 7500, 10000, 10000, 10000,
]);

/**
 * The creature `def` upgrades into, or null when it is already the top form.
 *
 * Keys are sorted before the scan. Content authored two upgrades of one base
 * creature would be a data bug either way, but an unsorted scan would resolve
 * it by map insertion order and so could differ between a bundle and a
 * from-source load of the same tree - a divergence that would show up as a
 * desynced state hash rather than as the content error it is.
 */
export function upgradeOf(
  content: ContentRegistry, def: CreatureDefId,
): CreatureDef | null {
  for (const id of [...content.creatures.keys()].sort()) {
    const d = content.creatures.get(id);
    if (d !== undefined && d.upgradeOf === def) return d;
  }
  return null;
}

/**
 * The cost of turning `count` of `from` into `to`, scaled by `scaleBp`.
 *
 * Integer throughout, and truncated once at the end rather than per creature:
 * rounding each creature separately would make ten upgrades cost more than one
 * upgrade of ten, which a player can see.
 */
export function upgradePrice(
  from: CreatureDef, to: CreatureDef, count: number, scaleBp: number,
): Partial<Resources> {
  const out: Record<string, number> = {};
  for (const r of RESOURCES) {
    const diff = (to.cost[r] ?? 0) - (from.cost[r] ?? 0);
    if (diff <= 0) continue;
    const total = Math.trunc((diff * count * scaleBp) / FULL_PRICE_BP);
    if (total > 0) out[r] = total;
  }
  return out as Partial<Resources>;
}

/** One stack that could be upgraded, and the bill for doing it. */
export interface UpgradeOffer {
  readonly slot: number;
  readonly from: CreatureDef;
  readonly to: CreatureDef;
  readonly count: number;
  readonly cost: Partial<Resources>;
}

/**
 * Every upgradeable stack in `slots`.
 *
 * `permits` decides which upgrades this place offers at all: a Warcraft Keep takes
 * anything, a town only what its own dwellings produce. Passing the predicate
 * in - rather than filtering afterwards - keeps a town from quoting a price for
 * an upgrade it cannot perform.
 *
 * `scaleBp` is a function of the *base* creature rather than a single number,
 * because a Warcraft Keep's discount is per tier: one visit can upgrade a stack of
 * spearwardens for nothing and a stack of angels at full price, so the two stacks
 * cannot share a scale. A town passes a function that ignores its argument.
 */
export function upgradeOffers(
  content: ContentRegistry,
  slots: readonly ({ readonly def: CreatureDefId; readonly count: number } | null)[],
  permits: (to: CreatureDef) => boolean,
  scaleBp: (from: CreatureDef) => number,
): readonly UpgradeOffer[] {
  const out: UpgradeOffer[] = [];
  for (let i = 0; i < slots.length; i++) {
    const s = slots[i];
    if (s === null || s === undefined || s.count <= 0) continue;
    const from = content.creatures.get(s.def);
    if (from === undefined) continue;
    const to = upgradeOf(content, s.def);
    if (to === null || !permits(to)) continue;
    out.push({
      slot: i,
      from,
      to,
      count: s.count,
      cost: upgradePrice(from, to, s.count, scaleBp(from)),
    });
  }
  return out;
}
